import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAppStore } from 'context/AppStore';
import { billingAPI } from '../../api/client';
import { useAuth } from 'context/AuthContext';
import { Button } from '../ui';

const ROUTE_LABELS = {
  '/':           'Dashboard',
  '/publish':    'Publicar',
  '/posts':      'Publicaciones',
  '/analytics':  'Analytics',
  '/projects':   'Proyectos',
  '/accounts':   'Cuentas de Redes',
  '/providers':  'Proveedores de IA',
  '/settings':   'Configuración',
  '/users':      'Usuarios',
};

export default function Topbar() {
  const { pathname } = useLocation();
  const { toggleSidebar, sidebarOpen, notifications, markAllRead } = useAppStore();
  const { user, logout } = useAuth();
  const [showNotif, setShowNotif] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    billingAPI.getSubscription().then(r => setSubscription(r.data)).catch(() => {});
  }, []);

  const creditPercent = subscription?.credit_percent || 0;
  const creditColor = creditPercent >= 95 ? '#ef4444' : creditPercent >= 85 ? '#f97316' : creditPercent >= 70 ? '#f59e0b' : '#10b981';
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(
    window.matchMedia('(display-mode: standalone)').matches
  );

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setPwaInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (pwaPrompt) {
      pwaPrompt.prompt();
      const { outcome } = await pwaPrompt.userChoice;
      if (outcome === 'accepted') { setPwaInstalled(true); setPwaPrompt(null); }
    }
    setShowInstall(false);
  };
  const unread = notifications.filter(n => !n.read).length;
  const label = ROUTE_LABELS[pathname] || 'TX-Flow AI';

  return (
    <header style={{
      height: 'var(--topbar-height)',
      background: 'var(--bg-surface)',
      borderBottom: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      paddingTop: 'env(safe-area-inset-top)',
      paddingLeft: 'max(16px, env(safe-area-inset-left))',
      paddingRight: 'max(16px, env(safe-area-inset-right))',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 150,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      flexShrink: 0,
      boxShadow: '0 1px 8px rgba(0,0,0,0.15)',
    }}>

      {/* Izquierda */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={toggleSidebar}
          style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            color: 'var(--text-primary)', cursor: 'pointer',
            fontSize: '18px', padding: '0', borderRadius: '8px', lineHeight: 1,
            height: '36px', width: '36px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 'var(--shadow-sm)',
          }}
          aria-label="Menú"
        >
          {sidebarOpen ? '✕' : '☰'}
        </button>

        {/* Logo visible solo en móvil cuando sidebar cerrado */}
        <div className="show-mobile" style={{
          display: 'none', alignItems: 'center', gap: '8px',
        }}>
          <img src="/thitan-logo.png" alt="TX-Flow AI" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: '800', fontSize: '13px' }}>TX-Flow AI</span>
        </div>

        <h1 className="hide-mobile" style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '700' }}>{label}</h1>
      </div>

      {/* Derecha */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

        {/* Cerrar sesión — solo mobile */}
        <button
          onClick={logout}
          className="show-mobile"
          title="Cerrar sesión"
          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px', borderRadius: '8px', display: 'none', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(239,68,68,0.4)', height: '36px', width: '36px' }}
        >⏻</button>

        {/* Publicar — oculto en móvil (está en bottom nav) */}
        <div className="hide-mobile" style={{ display: 'flex' }}>
          <Link to="/publish" style={{ textDecoration: 'none' }}>
            <Button size="sm" icon="⚡">Publicar</Button>
          </Link>
        </div>

        {/* Instalar PWA */}
        {!pwaInstalled && (
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowInstall(!showInstall)}
              title="Instalar aplicación"
              style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                color: 'var(--text-secondary)', cursor: 'pointer',
                width: '40px', height: '40px', borderRadius: 'var(--radius-md)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '16px', flexShrink: 0,
              }}
            >
              📲
            </button>

            {showInstall && (
              <div className="animate-scaleIn" style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                width: '280px', maxWidth: 'calc(100vw - 32px)',
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
                zIndex: 200, overflow: 'hidden',
              }}>
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <img src="/thitan-logo.png" alt="TX-Flow AI" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '14px' }}>Instalar TX-Flow AI</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Acceso rápido desde tu dispositivo</div>
                    </div>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '14px' }}>
                    Instala la app en tu PC o móvil para acceder sin navegador, recibir notificaciones y publicar más rápido.
                  </p>
                  {pwaPrompt ? (
                    <button onClick={handleInstall} style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--gradient-primary)', border: 'none', color: '#fff', fontWeight: '700', fontSize: '13px', fontFamily: 'var(--font-body)', cursor: 'pointer' }}>
                      📲 Instalar ahora
                    </button>
                  ) : (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '10px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', lineHeight: 1.6 }}>
                      <strong>En iOS:</strong> Safari → Compartir → "Añadir a pantalla de inicio"<br />
                      <strong>En Android:</strong> Chrome → Menú → "Instalar app"<br />
                      <strong>En PC:</strong> Busca el ícono ⊕ en la barra de URL del navegador
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notificaciones */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => { setShowNotif(!showNotif); if (!showNotif && unread > 0) markAllRead(); }}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              color: 'var(--text-secondary)', cursor: 'pointer',
              fontSize: '16px', padding: '7px 10px', borderRadius: 'var(--radius-md)',
              position: 'relative', lineHeight: 1,
              minHeight: '44px', minWidth: '44px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            🔔
            {unread > 0 && (
              <span style={{
                position: 'absolute', top: '6px', right: '6px',
                width: '8px', height: '8px', borderRadius: '50%',
                background: '#ef4444', border: '1px solid var(--bg-surface)',
              }} />
            )}
          </button>

          {showNotif && (
            <div
              className="animate-scaleIn"
              style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                width: '300px',
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
                zIndex: 200, overflow: 'hidden',
                // En móvil, que no se salga por la derecha
                maxWidth: 'calc(100vw - 32px)',
              }}
            >
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '700', fontSize: '14px' }}>Notificaciones</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Todo al día ✓</span>
              </div>
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                🎉 No tienes notificaciones pendientes
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Banner de vencimiento próximo */}
      {subscription && (() => {
        const end = new Date(subscription.current_period_end + (subscription.current_period_end.endsWith('Z') ? '' : 'Z'));
        const now = new Date();
        const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        if (daysLeft > 7) return null;
        const isExpired = daysLeft <= 0;
        const color = isExpired ? '#ef4444' : daysLeft <= 3 ? '#f97316' : '#f59e0b';
        const bg = isExpired ? '#ef444408' : daysLeft <= 3 ? '#f9731608' : '#f59e0b08';
        return (
          <div style={{
            position: 'absolute', top: 'var(--topbar-height)', left: 0, right: 0,
            background: bg, borderBottom: `1px solid ${color}30`,
            padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            zIndex: 99, gap: '12px', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>{isExpired ? '⚠️' : '📅'}</span>
              <div>
                <span style={{ fontSize: '13px', fontWeight: '600', color }}>
                  {isExpired
                    ? 'Tu membresía ha vencido'
                    : daysLeft === 1
                      ? 'Tu membresía vence mañana'
                      : `Tu membresía vence en ${daysLeft} días`}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                  {isExpired
                    ? 'Renueva para continuar usando las funciones de IA y publicación.'
                    : `Se renueva el ${end.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}. Renueva para no perder el acceso.`}
                </span>
              </div>
            </div>
            <Link to="/pricing" style={{ textDecoration: 'none', flexShrink: 0 }}>
              <button style={{
                padding: '6px 16px', borderRadius: '99px', border: `1px solid ${color}`,
                background: `${color}15`, color, fontSize: '12px', fontWeight: '700',
                cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
              }}>
                {isExpired ? 'Renovar ahora' : 'Ver planes →'}
              </button>
            </Link>
          </div>
        );
      })()}
    </header>
  );
}
